/**
 * MemberInvitationIA — Invitation IA d'un futur membre (Phase 1).
 * Rend les boutons « Invitations IA » (revue/validation) + « Inviter par IA »
 * ainsi que leurs modales. Réutilisé dans l'espace Admin/Super Admin et dans
 * l'espace COOPER'S (« Mes Membres »).
 *
 * - COOPER'S / expert : l'envoi débite 100 crédits (géré côté backend).
 * - Admin / super utilisateur : gratuit.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { RemisePibSetting, InvitationPaiements, ContributionSuspensiveSetting } from './invitation/InvitationPaiements';
import { ImpayesTable } from './invitation/ImpayesTable';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Clock, Sparkles, Eye, Loader2, X, Calculator } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_LABELS = {
  kbis: 'Extrait Kbis',
  piece_identite: "Pièce d'identité",
  bilan: 'Dernier bilan',
};

export const MemberInvitationIA = ({ getAuthHeader, onChanged, userRole = 'cooper' }) => {
  const isPaid = ['cooper', 'expert'].includes((userRole || '').toLowerCase());
  const navigate = useNavigate();

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', raison_sociale: '' });
  const [sendingInvite, setSendingInvite] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [invActionId, setInvActionId] = useState(null);

  // Aperçu des pièces
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLabel, setPreviewLabel] = useState('');
  const [previewLoadingKey, setPreviewLoadingKey] = useState(null);

  const fetchInvitations = async () => {
    try {
      const res = await axios.get(`${API}/member-invitation/admin/pending`, { headers: getAuthHeader() });
      setInvitations(res.data?.invitations || []);
    } catch {
      toast.error('Erreur chargement des invitations');
    }
  };

  const handleSendInvite = async () => {
    if (!inviteData.email || !inviteData.email.includes('@')) {
      toast.error('Saisissez une adresse email valide');
      return;
    }
    setSendingInvite(true);
    try {
      await axios.post(`${API}/member-invitation/create`, inviteData, { headers: getAuthHeader() });
      toast.success(`Invitation envoyée à ${inviteData.email}`);
      setShowInviteModal(false);
      setInviteData({ email: '', raison_sociale: '' });
      if (onChanged) onChanged();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur pendant l'envoi de l'invitation";
      toast.error(typeof msg === 'string' ? msg : "Erreur pendant l'envoi");
    } finally {
      setSendingInvite(false);
    }
  };

  const handleRelance = async (tok) => {
    setInvActionId(tok);
    try {
      await axios.post(`${API}/member-invitation/admin/${tok}/relance`, {}, { headers: getAuthHeader() });
      toast.success('Relance envoyée');
      fetchInvitations();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur relance');
    } finally { setInvActionId(null); }
  };

  const handleValidate = async (tok) => {
    setInvActionId(tok);
    try {
      await axios.post(`${API}/member-invitation/admin/${tok}/validate`, {}, { headers: getAuthHeader() });
      toast.success('Membre validé et activé');
      fetchInvitations();
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur validation');
    } finally { setInvActionId(null); }
  };

  const handlePreview = async (tok, type) => {
    const key = `${tok}-${type}`;
    setPreviewLoadingKey(key);
    try {
      const res = await axios.get(`${API}/member-invitation/admin/${tok}/document/${type}`, {
        headers: getAuthHeader(), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(res.data);
      setPreviewUrl(url);
      setPreviewLabel(DOC_LABELS[type] || type);
    } catch {
      toast.error('Impossible de charger la pièce');
    } finally {
      setPreviewLoadingKey(null);
    }
  };

  const closePreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewLabel('');
  };

  return (
    <>
      <Button
        onClick={() => { setShowReviewModal(true); fetchInvitations(); }}
        variant="outline"
        className="border-violet-300 text-violet-700 hover:bg-violet-50"
        data-testid="review-invitations-btn"
      >
        <Clock className="h-4 w-4 mr-2" />Invitations
      </Button>
      <Button
        onClick={() => setShowInviteModal(true)}
        className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white"
        data-testid="invite-ia-member-btn"
      >
        <Sparkles className="h-4 w-4 mr-2" />Invitation IA RCDS
        {isPaid && <span className="ml-2 text-[10px] bg-white/25 rounded-full px-1.5 py-0.5">100 crédits</span>}
      </Button>

      {showInviteModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="invite-ia-modal">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-violet-600" />
              <h3 className="text-lg font-semibold">Inviter un futur membre</h3>
            </div>
            <p className="text-sm text-slate-500">
              Un email avec un questionnaire (profil + pièces : Kbis, identité, bilan) sera envoyé.
              Le membre sera créé automatiquement en attente.
              {isPaid && <span className="block mt-1 font-medium text-violet-700">Cette invitation débite 100 crédits de votre compte.</span>}
            </p>
            <div className="space-y-3">
              <input type="email" placeholder="Email du futur membre *" value={inviteData.email}
                onChange={(e) => setInviteData((p) => ({ ...p, email: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="invite-email-input" />
              <input type="text" placeholder="Raison sociale (optionnel)" value={inviteData.raison_sociale}
                onChange={(e) => setInviteData((p) => ({ ...p, raison_sociale: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="invite-raison-input" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowInviteModal(false)} data-testid="invite-cancel-btn">Annuler</Button>
              <Button onClick={handleSendInvite} disabled={sendingInvite} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="invite-send-btn">
                {sendingInvite ? 'Envoi…' : "Envoyer l'invitation"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showReviewModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="review-invitations-modal">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2"><Clock className="h-5 w-5 text-violet-600" />Invitations &amp; validation des dossiers</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowReviewModal(false)} data-testid="review-close-btn">✕</Button>
            </div>
            {['admin', 'super_admin'].includes(userRole) && <div className="mb-3"><ImpayesTable getAuthHeader={getAuthHeader} defaultProduit="rcds" /></div>}
            {['admin', 'super_admin'].includes(userRole) && <RemisePibSetting getAuthHeader={getAuthHeader} />}
            {userRole === 'super_admin' && <ContributionSuspensiveSetting getAuthHeader={getAuthHeader} />}
            {invitations.length === 0 ? (
              <p className="text-sm text-slate-400 py-8 text-center">Aucune invitation pour le moment.</p>
            ) : (
              <div className="space-y-2">
                {invitations.map((inv) => (
                  <div key={inv.token} className="border rounded-lg p-3 space-y-2" data-testid={`invitation-row-${inv.token}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{inv.raison_sociale || inv.email}</p>
                        <p className="text-xs text-slate-500 truncate">{inv.email} · {(inv.documents || []).length} pièce(s)</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${inv.status === 'valide' ? 'bg-emerald-100 text-emerald-700' : inv.status === 'complete' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                          {inv.status === 'valide' ? 'Validé' : inv.status === 'complete' ? 'À valider' : 'En attente'}
                        </span>
                        {inv.status !== 'complete' && inv.status !== 'valide' && (
                          <Button size="sm" variant="outline" disabled={invActionId === inv.token} onClick={() => handleRelance(inv.token)} data-testid={`relance-btn-${inv.token}`}>Relancer</Button>
                        )}
                        {inv.status === 'complete' && (
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={invActionId === inv.token} onClick={() => handleValidate(inv.token)} data-testid={`validate-btn-${inv.token}`}>Valider</Button>
                        )}
                        {inv.membre_id && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                            onClick={() => navigate(`/calculateur-batico?membre_id=${inv.membre_id}&ia_membre=${inv.membre_id}&from=ia_activites`)}
                            data-testid={`ia-activites-btn-${inv.token}`}
                          >
                            <Calculator className="h-3.5 w-3.5 mr-1" />Simulateur IA activités
                          </Button>
                        )}
                      </div>
                    </div>
                    <InvitationPaiements inv={inv} getAuthHeader={getAuthHeader} />
                    {(inv.documents || []).length > 0 && (
                      <div className="flex flex-wrap gap-2 pt-1 border-t border-slate-100">
                        {(inv.documents || []).map((type) => (
                          <Button
                            key={type}
                            size="sm"
                            variant="outline"
                            className="text-xs border-slate-300 text-slate-600 hover:bg-slate-50"
                            disabled={previewLoadingKey === `${inv.token}-${type}`}
                            onClick={() => handlePreview(inv.token, type)}
                            data-testid={`preview-doc-${type}-${inv.token}`}
                          >
                            {previewLoadingKey === `${inv.token}-${type}`
                              ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                              : <Eye className="h-3.5 w-3.5 mr-1" />}
                            {DOC_LABELS[type] || type}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {previewUrl && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" data-testid="doc-preview-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h4 className="font-semibold text-slate-800 flex items-center gap-2"><Eye className="h-4 w-4" />{previewLabel}</h4>
              <Button variant="ghost" size="sm" onClick={closePreview} data-testid="doc-preview-close-btn"><X className="h-4 w-4" /></Button>
            </div>
            <iframe title={previewLabel} src={previewUrl} className="flex-1 w-full rounded-b-xl" />
          </div>
        </div>
      )}
    </>
  );
};

export default MemberInvitationIA;
